/** A review's message, shown as the comments the operator wrote - each with where it was
 * made and how it stands - rather than the request the agent was given, which the turn's
 * AI trace holds. */
import { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";

import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import {
  useReviewComments,
  useWithdrawReviewComment,
} from "../api/hooks/useReview";
import { QK } from "../api/queryKeys";
import { ReviewCommentItem } from "./ReviewBar";
import styles from "./Review.module.css";

export const ReviewSubmission = ({
  runId,
  projectId,
  commentIds,
}: {
  runId: string;
  projectId: string | null;
  commentIds: string[];
}) => {
  const { data } = useReviewComments({ run_id: runId });
  const phases = data?.phases ?? {};
  const comments = (data?.comments ?? []).filter((c) =>
    commentIds.includes(c.comment_id),
  );
  const n = commentIds.length;
  return (
    <div className={styles.submission}>
      <p className={styles.submissionHead}>
        Review · {n} comment{n === 1 ? "" : "s"}
      </p>
      <ul className={styles.threadList}>
        {comments.map((c) => (
          <ReviewCommentItem
            key={c.comment_id}
            comment={c}
            projectId={projectId}
            phase={phases[c.comment_id]}
            linkConversation={false}
            workedBy={
              data?.worked_by?.[`${c.settled_by_run_id}:${c.project_id}`]
            }
          />
        ))}
      </ul>
    </div>
  );
};

/** Comments sent while the agent was busy: they reach it together, in one message, when
 * it is free. Until then they are shown here rather than in the transcript. */
export const QueuedReviewComments = ({
  runId,
  projectId,
}: {
  runId: string;
  projectId: string | null;
}) => {
  const { data } = useReviewComments({ run_id: runId });
  const { user } = useKindeAuth();
  const withdraw = useWithdrawReviewComment();
  const phases = data?.phases ?? {};
  // Between its turns the conversation is not polled: when one of its comments moves on
  // (delivered, applied, replied to), read it again.
  const qc = useQueryClient();
  const moved = (data?.comments ?? [])
    .filter((c) => c.conversation_id === runId)
    .map((c) => `${c.comment_id}:${c.status}:${phases[c.comment_id] ?? ""}`)
    .join(",");
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (seen.current !== null && seen.current !== moved) {
      void qc.invalidateQueries([QK.statefulRun, runId]);
    }
    seen.current = moved;
  }, [moved, qc, runId]);
  // Not yet given to the agent; an agreed change waiting on an update has been.
  const queued = (data?.comments ?? []).filter(
    (c) =>
      c.conversation_id === runId &&
      c.run_id === null &&
      !c.agreed_change &&
      phases[c.comment_id] === "queued",
  );
  if (queued.length === 0) return null;
  const n = queued.length;
  return (
    <div className={`${styles.submission} ${styles.queued}`}>
      <p className={styles.submissionHead}>
        Queued · {n} comment{n === 1 ? "" : "s"}, sent to the agent together
        once it has finished with your earlier comments
      </p>
      <ul className={styles.threadList}>
        {queued.map((c) => (
          <li key={c.comment_id} className={styles.queuedItem}>
            <ul className={styles.threadList}>
              <ReviewCommentItem
                comment={c}
                projectId={projectId}
                phase="queued"
                linkConversation={false}
              />
            </ul>
            <button
              type="button"
              className={styles.link}
              disabled={withdraw.isLoading}
              onClick={() =>
                withdraw.mutate({
                  comment_id: c.comment_id,
                  actor_email: user?.email ?? "",
                })
              }
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      {withdraw.error && (
        <p className={styles.queuedError}>{withdraw.error.message}</p>
      )}
    </div>
  );
};
